export interface PhoneCountryOption {
  code: string;
  label: string;
  dialCode: string;
  example: string;
}

export const DEFAULT_PHONE_COUNTRY_CODE = 'PT';
export const OTHER_PHONE_COUNTRY_CODE = 'OTHER';

export const PHONE_COUNTRY_OPTIONS: readonly PhoneCountryOption[] = [
  { code: 'PT', label: 'Portugal', dialCode: '+351', example: '912 345 678' },
  { code: 'ES', label: 'Espanha', dialCode: '+34', example: '612 345 678' },
  { code: 'FR', label: 'Franca', dialCode: '+33', example: '6 12 34 56 78' },
  { code: 'GB', label: 'Reino Unido', dialCode: '+44', example: '7123 456789' },
  { code: 'IE', label: 'Irlanda', dialCode: '+353', example: '85 123 4567' },
  { code: 'DE', label: 'Alemanha', dialCode: '+49', example: '1512 3456789' },
  { code: 'CH', label: 'Suica', dialCode: '+41', example: '79 123 45 67' },
  { code: 'LU', label: 'Luxemburgo', dialCode: '+352', example: '621 123 456' },
  { code: 'BE', label: 'Belgica', dialCode: '+32', example: '470 12 34 56' },
  { code: 'NL', label: 'Paises Baixos', dialCode: '+31', example: '6 12345678' },
  { code: 'IT', label: 'Italia', dialCode: '+39', example: '312 345 6789' },
  { code: 'AT', label: 'Austria', dialCode: '+43', example: '664 1234567' },
  { code: 'BR', label: 'Brasil', dialCode: '+55', example: '11 91234 5678' },
  {
    code: 'US_CA',
    label: 'Estados Unidos / Canada',
    dialCode: '+1',
    example: '201 555 0123',
  },
  { code: 'AO', label: 'Angola', dialCode: '+244', example: '923 456 789' },
  { code: 'MZ', label: 'Mocambique', dialCode: '+258', example: '82 123 4567' },
  { code: 'CV', label: 'Cabo Verde', dialCode: '+238', example: '991 23 45' },
  { code: 'GW', label: 'Guine-Bissau', dialCode: '+245', example: '955 123 456' },
  {
    code: 'ST',
    label: 'Sao Tome e Principe',
    dialCode: '+239',
    example: '981 2345',
  },
  { code: 'TL', label: 'Timor-Leste', dialCode: '+670', example: '7723 4567' },
  { code: 'ZA', label: 'Africa do Sul', dialCode: '+27', example: '82 123 4567' },
  { code: OTHER_PHONE_COUNTRY_CODE, label: 'Outro pais', dialCode: '', example: '+351 912 345 678' },
] as const;

interface ParsedPhoneValue {
  countryCode: string;
  nationalNumber: string;
}

const phoneCountryOptionsByCode = new Map(
  PHONE_COUNTRY_OPTIONS.map((option) => [option.code, option])
);

const phoneCountryOptionsByDialCode = PHONE_COUNTRY_OPTIONS.filter(
  (option) => option.dialCode
).sort((left, right) => right.dialCode.length - left.dialCode.length);

function collapsePhoneWhitespace(value: string) {
  return value.trim().replace(/\s+/g, ' ');
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getResolvedFallbackCountryCode(countryCode?: string) {
  if (!countryCode || countryCode === OTHER_PHONE_COUNTRY_CODE) {
    return DEFAULT_PHONE_COUNTRY_CODE;
  }

  return phoneCountryOptionsByCode.has(countryCode)
    ? countryCode
    : DEFAULT_PHONE_COUNTRY_CODE;
}

export function getPhoneCountryOption(countryCode?: string) {
  return phoneCountryOptionsByCode.get(countryCode ?? DEFAULT_PHONE_COUNTRY_CODE) ?? null;
}

export function parsePhoneValue(
  value: string | null | undefined,
  fallbackCountryCode = DEFAULT_PHONE_COUNTRY_CODE
): ParsedPhoneValue {
  const normalizedValue = collapsePhoneWhitespace(value ?? '');
  const resolvedFallbackCountryCode = getResolvedFallbackCountryCode(
    fallbackCountryCode
  );

  if (!normalizedValue) {
    return {
      countryCode: resolvedFallbackCountryCode,
      nationalNumber: '',
    };
  }

  if (!/^(?:\+|00)\d/.test(normalizedValue)) {
    return {
      countryCode: resolvedFallbackCountryCode,
      nationalNumber: normalizedValue,
    };
  }

  const normalizedDigits = normalizedValue
    .replace(/^(?:\+|00)/, '')
    .replace(/\D/g, '');

  const matchedCountry = phoneCountryOptionsByDialCode.find((option) =>
    normalizedDigits.startsWith(option.dialCode.replace('+', ''))
  );

  if (!matchedCountry) {
    return {
      countryCode: OTHER_PHONE_COUNTRY_CODE,
      nationalNumber: normalizedValue,
    };
  }

  const dialCodeDigits = matchedCountry.dialCode.replace('+', '');
  const localNumber = collapsePhoneWhitespace(
    normalizedValue.replace(
      new RegExp(`^(?:\\+|00)${escapeRegExp(dialCodeDigits)}(?:[\\s-]*)`),
      ''
    )
  );

  return {
    countryCode: matchedCountry.code,
    nationalNumber: localNumber,
  };
}

export function formatPhoneValue(
  nationalNumber: string | null | undefined,
  countryCode = DEFAULT_PHONE_COUNTRY_CODE
) {
  const normalizedNationalNumber = collapsePhoneWhitespace(nationalNumber ?? '');

  if (!normalizedNationalNumber) {
    return '';
  }

  if (countryCode === OTHER_PHONE_COUNTRY_CODE) {
    return normalizedNationalNumber;
  }

  const country = getPhoneCountryOption(countryCode);

  if (!country?.dialCode) {
    return normalizedNationalNumber;
  }

  return `${country.dialCode} ${normalizedNationalNumber}`.trim();
}

export function getPhoneInputValue(
  value: string | null | undefined,
  selectedCountryCode: string,
  fallbackCountryCode = DEFAULT_PHONE_COUNTRY_CODE
) {
  const normalizedValue = collapsePhoneWhitespace(value ?? '');

  if (!normalizedValue) {
    return '';
  }

  if (selectedCountryCode === OTHER_PHONE_COUNTRY_CODE) {
    return normalizedValue;
  }

  const parsedValue = parsePhoneValue(
    normalizedValue,
    selectedCountryCode === OTHER_PHONE_COUNTRY_CODE
      ? fallbackCountryCode
      : selectedCountryCode
  );

  return parsedValue.countryCode === OTHER_PHONE_COUNTRY_CODE
    ? normalizedValue
    : parsedValue.nationalNumber;
}

export function normalizePhoneValue(
  value: string | null | undefined,
  fallbackCountryCode = DEFAULT_PHONE_COUNTRY_CODE
) {
  const normalizedValue = collapsePhoneWhitespace(value ?? '');

  if (!normalizedValue) {
    return null;
  }

  const parsedValue = parsePhoneValue(normalizedValue, fallbackCountryCode);

  if (parsedValue.countryCode === OTHER_PHONE_COUNTRY_CODE) {
    return normalizedValue;
  }

  if (!parsedValue.nationalNumber) {
    return null;
  }

  return formatPhoneValue(parsedValue.nationalNumber, parsedValue.countryCode);
}

export function hasPhoneValue(
  value: string | null | undefined,
  fallbackCountryCode = DEFAULT_PHONE_COUNTRY_CODE
) {
  return normalizePhoneValue(value, fallbackCountryCode) !== null;
}
